import type {
  AccordionContentPassThroughOptions,
  AccordionHeaderPassThroughOptions,
  AccordionPanelPassThroughOptions,
} from 'primevue';
import type { ComputedRef, Ref } from 'vue';
import {
  computed,

  ref,

  watchEffect,
} from 'vue';
import { useRoute } from 'vue-router';

import type { ITableBaseRecordType } from '@modules/tables/components/VTable/types';
import type { ITableDataResponseColumn } from '@modules/tables/types';
import { QA_TABLES } from '@modules/tables/constants';
import { isNumber } from '@utils/guards';
import { useLocalStorage, watchImmediate } from '@vueuse/core';

export interface TableMobileComposable {
  activeRow: Ref<number[]>;
  isExpandedAll: Ref<boolean>;
  previewColumnsStorage: Ref<string[]>;
  previewColumns: ComputedRef<ITableDataResponseColumn[]>;
  isMultiplePreviewColumns: ComputedRef<boolean>;
  isAllSelected: ComputedRef<boolean>;
  getAccordionPanelPT(index?: number, isMultiplePreview?: boolean, isRowSelected?: boolean): AccordionPanelPassThroughOptions;
  getAccordionHeaderPT(index?: number, isMultiplePreview?: boolean, isRowSelected?: boolean): AccordionHeaderPassThroughOptions;
  getAccordionContentPT(index?: number, isMultiplePreview?: boolean, isRowSelected?: boolean): AccordionContentPassThroughOptions;
  toggleAll(): void;
  isActiveTab(index: number): boolean;
  isRowSelected(id: number): boolean;
  onPreviewColumnUpdate(columnName: string, active?: boolean): void;
}

export default function useTableMobile<TColumn>(
  columns: Ref<ITableDataResponseColumn[] | undefined>,
  list: Ref<TColumn[] | undefined>,
  selection: Ref<ITableBaseRecordType[]>,
  selectionIdsOnPage: Ref<number[]>,
): TableMobileComposable {
  const route = useRoute();
  const previewColumnsStorage = useLocalStorage<string[]>(String(route.name), [], { writeDefaults: false });

  const activeRow = ref<number[]>([]);
  const isExpandedAll = ref<boolean>(false);

  const previewColumns = computed<ITableDataResponseColumn[]>(() => {
    const cols = columns.value?.filter((column) => previewColumnsStorage.value.includes(column.name)) ?? [];

    if (!cols.length && columns.value) {
      cols.push(columns.value[0]);
    }

    return cols;
  });

  const isMultiplePreviewColumns = computed(() => previewColumns.value?.length > 1);

  const isAllSelected = computed(() => selectionIdsOnPage.value.length > 0 && selectionIdsOnPage.value.length === list.value?.length);

  watchEffect(() => {
    if (activeRow.value.length === list.value?.length) {
      isExpandedAll.value = true;
    } else if (activeRow.value.length === 0) {
      isExpandedAll.value = false;
    }
  });

  watchImmediate(columns, () => {
    if (!columns.value)
      return;

    const column = columns.value.find((col) => col.columnOptions?.defaultMobilePreview);

    if (column && !previewColumnsStorage.value.length) {
      previewColumnsStorage.value = [column.name];
    }
  });

  function toggleAll(): void {
    isExpandedAll.value = !isExpandedAll.value;

    if (isExpandedAll.value) {
      activeRow.value = list.value?.map((val, i) => i) ?? [];
    } else {
      activeRow.value = [];
    }
  }

  function isActiveTab(index: number): boolean {
    return activeRow.value.includes(index);
  }

  function isRowSelected(id: number): boolean {
    return !!selection.value.find((item) => item.id === id);
  }

  function onPreviewColumnUpdate(columnName: string, active?: boolean): void {
    if (active) {
      previewColumnsStorage.value.push((columnName));
    } else {
      previewColumnsStorage.value = previewColumnsStorage.value.filter((name) => columnName !== name);
    }
  }

  function getAccordionPanelPT(index?: number, isRowSelectedInner?: boolean): AccordionPanelPassThroughOptions {
    return {
      root(): Record<string, string> | (() => string) {
        return isNumber(index)
          ? (): string => {
              let cl;
              if (isRowSelectedInner) {
                cl = 'bg-primary-100';
              } else {
                cl = index % 2 === 0 ? 'bg-surface-100' : 'bg-content-color';
              }

              return `${cl} mb-3 relative`;
            }
          : { 'class': 'bg-primary-200', 'data-test-el': QA_TABLES.bodyRowTotal };
      },
    };
  }

  function getAccordionHeaderPT(index?: number, isMultiplePreview?: boolean, isRowSelectedInner?: boolean): AccordionHeaderPassThroughOptions {
    return {
      root(): Record<string, string | number | string[] | undefined> {
        return {
          'class': [
            'min-h-[50px] p-0 bg-transparent border-b relative',
            isMultiplePreview ? 'pr-0' : 'pr-8',
            isRowSelectedInner ? 'bg-primary/10' : 'bg-transparent',
          ],
          'data-test-el': QA_TABLES.mobileAccordionItemToggle,
          'data-test-attr-accordion-item-index': index,
        };
      },
      toggleicon: { class: 'mb-auto mt-5 absolute top-[0px] right-[12px] w-5 h-5' },
    };
  }

  function getAccordionContentPT(): AccordionContentPassThroughOptions {
    return {
      content: { class: 'p-0 bg-transparent' },
    };
  }

  return {
    activeRow,
    isExpandedAll,
    previewColumnsStorage,
    previewColumns,
    isMultiplePreviewColumns,
    isAllSelected,
    getAccordionPanelPT,
    getAccordionHeaderPT,
    getAccordionContentPT,
    toggleAll,
    isActiveTab,
    isRowSelected,
    onPreviewColumnUpdate,
  };
}
